import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from '@spot/shared';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import uk from './locales/uk.json';

type Translation = typeof uk;

/**
 * One JSON file per locale. Ukrainian (the default) is in the main bundle; others load on
 * demand (T-1003, initial JS budget). To add a language: add its code to `SUPPORTED_LOCALES` in
 * packages/shared (the `satisfies` below then fails to compile until a loader is added here),
 * and create `locales/<code>.json` with every key (enforced by i18n.test.ts).
 */
const LOADERS = {
	uk: () => Promise.resolve(uk),
	en: () => import('./locales/en.json').then((module) => module.default),
} as const satisfies Record<Locale, () => Promise<Translation>>;

export function loadTranslation(locale: Locale): Promise<Translation> {
	return LOADERS[locale]();
}

/** Telegram `language_code` (e.g. "en", "pt-br") → a supported UI locale. */
export function resolveUiLocale(languageCode: string | undefined): Locale {
	const base = languageCode?.toLowerCase().split('-')[0];
	return base !== undefined && isSupportedLocale(base) ? base : DEFAULT_LOCALE;
}

export async function initI18n(locale: Locale): Promise<typeof i18n> {
	const resources: Partial<Record<Locale, { translation: Translation }>> = {
		uk: { translation: uk },
	};
	resources[locale] = { translation: await loadTranslation(locale) };
	await i18n.use(initReactI18next).init({
		resources,
		lng: locale,
		fallbackLng: DEFAULT_LOCALE,
		supportedLngs: Object.keys(LOADERS),
		// React escapes output already; user text is always rendered as plain text.
		interpolation: { escapeValue: false },
		returnNull: false,
	});
	return i18n;
}

export async function setUiLocale(locale: Locale): Promise<void> {
	if (!i18n.hasResourceBundle(locale, 'translation')) {
		i18n.addResourceBundle(locale, 'translation', await loadTranslation(locale));
	}
	if (i18n.language !== locale) await i18n.changeLanguage(locale);
	document.documentElement.lang = locale;
}
